import * as React from "react";

import { Resources, useResource } from "Translations/Resources";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import { AccessRightCodes } from "Components/Models/AccessRights";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { Authorized } from "Routes/Authorized";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { Documents } from "Components/Shared/Documents/Documents";
import { EditIcon } from "Components/Shared/Icons/EditIcon";
import { EmployeeDetailBasicInfo } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailBasicInfo";
import { EmployeeDetailConfirmModal } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailConfirmModal";
import { EmployeeDetailDepartments } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailDepartments";
import { EmployeeDetailPositions } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailPositions";
import { EmployeesSideMenu } from "Components/HumanResources/Employees/EmployeesSideMenu";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { RootStateType } from "State/Store";
import { UnstyledLink } from "Components/Shared/Routes/UnstyledLink";
import { getHumanResourcesEmployeeDetailAsync } from "State/HumanResources/HumanResourcesReducer";
import { isNoU } from "Utils/ObjectUtils";
import styled from "styled-components";
import { EmployeeTypeCategoryID } from "Api/Api";

type RouteProps = {
  id: string;
};

type Props = {
  employeeTypeCategoryID: EmployeeTypeCategoryID;
};

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }

  .box-header-buttons {
    display: flex;

    .edit {
      margin-right: 10px;
    }
  }
`;

export const getEmployeeBaseUrl = (t: (resourcePath: string) => string) => {
  return t(Resources.Routes.Employees);
};

export const getEmployeeEditRight = (
  employeeTypeCategoryID: EmployeeTypeCategoryID,
) =>
  employeeTypeCategoryID === EmployeeTypeCategoryID.Internal
    ? AccessRightCodes.Core_HumanResources_Employee_Edit
    : AccessRightCodes.Core_HumanResources_ExternalSuppliers_Edit;

export const EmployeeDetailPage: React.FunctionComponent<Props> = props => {
  const dispatch = useDispatch();
  const { t } = useResource();
  const { params } = useRouteMatch<RouteProps>();
  const employeeID = params.id as any;
  const {
    employeeDetail: employee,
    isEmployeeDetailLoading: isLoading,
  } = useSelector((e: RootStateType) => e.humanResources);

  const editLink = employee
    ? `/${getEmployeeBaseUrl(t)}/${t(Resources.Routes.EmployeeEdit)}/${
        employee.employeeID
      }`
    : "";

  React.useEffect(() => {
    if (employeeID) {
      dispatch(getHumanResourcesEmployeeDetailAsync.request(employeeID));
    }
  }, [employeeID, dispatch]);

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const listTitle = "Kontakty";

    return [
      {
        name: listTitle,
        url: getEmployeeBaseUrl(t),
      },
      {
        name: t(Resources.HumanResources.Employees.Detail.Breadcrumb.Title),
      },
    ];
  };

  return (
    <StyledLayout>
      <AuthenticatedLayout
        title={`${employee?.firstName} ${employee?.lastName}` ?? ""}
        hasSideMenu={!isNoU(employeeID)}
        breadcrumbItems={getBreadcrumbItems()}
      >
        <EmployeesSideMenu
          employeeTypeCategoryID={props.employeeTypeCategoryID}
          employeeID={employeeID}
        />
        <div className={"container-small"}>
          <div className={"box detail"}>
            <div className={"box-header"}>
              {!isLoading && employee ? (
                t(Resources.HumanResources.Employees.Detail.Title)
              ) : (
                <PrimarySkeleton height={30} />
              )}

              <div className={"box-header-buttons"}>
                <Authorized
                  accessRights={[
                    getEmployeeEditRight(props.employeeTypeCategoryID),
                  ]}
                >
                  <PrimaryTooltip
                    title={t(Resources.HumanResources.Employees.Detail.Edit)}
                  >
                    <UnstyledLink
                      to={editLink}
                      className={"bl-btn bl-btn-orange edit"}
                    >
                      <EditIcon />
                    </UnstyledLink>
                  </PrimaryTooltip>
                  <EmployeeDetailConfirmModal employee={employee} />
                </Authorized>
              </div>
            </div>

            <div className={"box-content"}>
              {!isLoading && employee ? (
                <>
                  <EmployeeDetailBasicInfo employee={employee} />

                  <EmployeeDetailPositions employee={employee} />

                  <EmployeeDetailDepartments employee={employee} />

                  <Documents
                    documents={employee.documents}
                    title={t(
                      Resources.HumanResources.Employees.Detail.Documents.Title,
                    )}
                  />
                </>
              ) : (
                <>
                  <PrimarySkeleton height={120} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                </>
              )}
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    </StyledLayout>
  );
};
